import React, { useState, useCallback } from 'react';
import { List, Typography, Modal, Spin, Button, Image } from 'antd';
import { 
  FileOutlined, 
  FileImageOutlined, 
  FilePdfOutlined, 
  VideoCameraOutlined,
  DownloadOutlined,
  EyeOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { TicketFile } from '../types/ticket';
import { apiService } from '../services/api';

const { Text } = Typography;

interface FileAttachmentProps {
  files: TicketFile[];
  token?: string | null;
  showLabel?: boolean;
  label?: string;
  className?: string;
}

const FileAttachment: React.FC<FileAttachmentProps> = ({ 
  files, 
  token, 
  showLabel = true, 
  label = "Attachments:",
  className = ""
}) => {
  const [previewFile, setPreviewFile] = useState<TicketFile | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <FileImageOutlined />;
    if (fileType.startsWith('video/')) return <VideoCameraOutlined />;
    if (fileType === 'application/pdf') return <FilePdfOutlined />;
    return <FileOutlined />;
  };

  const isImage = (fileType: string) => {
    return fileType.startsWith('image/');
  };

  const isVideo = (fileType: string) => {
    return fileType.startsWith('video/');
  };

  const isPdf = (fileType: string) => {
    return fileType === 'application/pdf';
  };

  const handleFileClick = useCallback(async (file: TicketFile) => {
    setPreviewFile(file);
    setFileLoading(true);
    try {
      const url = await apiService.getFileStream(file.id, token);
      setFileUrl(url);
    } catch (error) {
      console.error('Error loading file:', error);
    } finally {
      setFileLoading(false);
    }
  }, [token]);

  const handleClosePreview = () => {
    setPreviewFile(null);
    setFileUrl(null);
  };

  const handleDownload = async (file: TicketFile) => {
    try {
      await apiService.downloadFile(file.id, file.name, token);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const renderFileItem = (file: TicketFile) => (
    <div className="file-item" onClick={() => handleFileClick(file)}>
      <span className="file-icon">{getFileIcon(file.fileType)}</span>
      <Text className="file-name" ellipsis>{file.name}</Text>
    </div>
  );

  if (!files || files.length === 0) return null;

  return (
    <div className={`file-attachment ${className}`}>
      {showLabel && (
        <Text type="secondary" className="file-attachment-label">
          {label}
        </Text>
      )}
      <List
        dataSource={files}
        renderItem={renderFileItem}
        className="file-list"
      />

      {/* Unified Preview Modal */}
      <Modal
        open={!!previewFile}
        onCancel={handleClosePreview}
        footer={[
          <Button 
            key="download" 
            icon={<DownloadOutlined />} 
            onClick={() => previewFile && handleDownload(previewFile)} 
            loading={fileLoading}
          >
            Download
          </Button>,
          <Button key="close" icon={<CloseOutlined />} onClick={handleClosePreview}>
            Close
          </Button>,
        ]}
        width="95%"
        centered
        destroyOnClose
        maskClosable={true}
        closable={true}
        zIndex={1000}
        bodyStyle={{ padding: isVideo(previewFile?.fileType || '') ? '0' : '24px' }}
      >
        {previewFile && (
          <div className="file-preview-container">
            <h3>{previewFile.name}</h3>
            {fileLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : fileUrl ? (
              <>
                {isImage(previewFile.fileType) && (
                  <div style={{ textAlign: 'center' }}>
                    <Image 
                      src={fileUrl} 
                      alt={previewFile.name}
                      style={{ maxWidth: '100%', maxHeight: '70vh' }}
                      preview={false}
                    />
                  </div>
                )}
                {isVideo(previewFile.fileType) && (
                  <video 
                    controls 
                    src={fileUrl}
                    style={{ width: '100%', maxHeight: '70vh' }}
                    autoPlay
                    playsInline
                  />
                )}
                {isPdf(previewFile.fileType) && (
                  <iframe 
                    src={fileUrl} 
                    width="100%" 
                    height="70vh"
                    style={{ border: 'none' }}
                    title={previewFile.name}
                  />
                )}
                {!isImage(previewFile.fileType) && !isVideo(previewFile.fileType) && !isPdf(previewFile.fileType) && (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <FileOutlined style={{ fontSize: '48px', color: '#8c8c8c', marginBottom: '16px' }} />
                    <Text>This file type cannot be previewed. Please download it to view.</Text>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text>Failed to load file.</Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FileAttachment; 